import React from 'react';
import { motion } from 'framer-motion';
import { heroOct } from '../data/teaser';
import type { Integrity, LayerId, RegionId } from '../types/teaser';
interface OctImageProps {
  /** Biomarker overlays to draw on the scan. */
  revealed?: LayerId[];
  /** Emphasise one overlay and mute the rest. */
  halo?: LayerId | null;
  /** Soft tinted highlight over an anatomical region. */
  region?: RegionId | null;
  /** Continuity of the outer bands when ELM / EZ overlays are shown. */
  integrity?: Integrity;
  /** Faint unexplained markers hinting at more information in the scan. */
  hints?: boolean;
  /** Sweeping analysis line. */
  scanning?: boolean;
  /** Slow ambient zoom. */
  drift?: boolean;
  /** Zoom into the central retina for small features. */
  zoom?: boolean;
  className?: string;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const CYSTS = [{
  left: 11,
  top: 26,
  width: 8,
  height: 23
}, {
  left: 21,
  top: 24,
  width: 7,
  height: 27
}, {
  left: 30,
  top: 26,
  width: 8,
  height: 25
}, {
  left: 39,
  top: 30,
  width: 9,
  height: 22
}, {
  left: 48,
  top: 36,
  width: 7,
  height: 16
}];
const FOCI = [{
  left: 20,
  top: 20
}, {
  left: 30,
  top: 18
}, {
  left: 37,
  top: 30
}, {
  left: 41,
  top: 37
}, {
  left: 46,
  top: 44
}, {
  left: 33,
  top: 45
}, {
  left: 25,
  top: 40
}, {
  left: 52,
  top: 42
}];
const HINTS = [{
  left: 26,
  top: 24
}, {
  left: 44,
  top: 40
}, {
  left: 63,
  top: 50
}];
const SRF = {
  left: 56,
  top: 46,
  width: 30,
  height: 8
};
const REGIONS: Record<RegionId, {
  left: number;
  top: number;
  width: number;
  height: number;
  color: string;
}> = {
  central: {
    left: 25,
    top: 16,
    width: 30,
    height: 38,
    color: '#4DA3F5'
  },
  peripheral: {
    left: 4,
    top: 18,
    width: 21,
    height: 36,
    color: '#4DA3F5'
  },
  foci: {
    left: 12,
    top: 14,
    width: 46,
    height: 38,
    color: '#F0B45E'
  },
  bands: {
    left: 4,
    top: 53,
    width: 92,
    height: 12,
    color: '#F08098'
  }
};
const BAND_SEGMENTS: Record<Integrity, [number, number][]> = {
  intact: [[4, 96]],
  partial: [[4, 38], [46, 62], [70, 96]],
  extensive: [[4, 24], [32, 40], [52, 58], [74, 82], [90, 96]]
};
function Band({
  top,
  segments,
  strong
}: {
  top: number;
  segments: [number, number][];
  strong: boolean;
}) {
  return <>
      {segments.map(([from, to], i) => <motion.span key={`${top}-${from}`} initial={{
      opacity: 0
    }} animate={{
      opacity: strong ? 1 : 0.65
    }} transition={{
      duration: 0.24,
      delay: i * 0.04,
      ease: EASE
    }} className="absolute rounded-full bg-[#F08098]" style={{
      left: `${from}%`,
      width: `${to - from}%`,
      top: `${top}%`,
      height: strong ? 3 : 2,
      boxShadow: strong ? '0 0 6px rgba(240,128,152,0.8)' : 'none'
    }} data-id="element-100" data-mp="u7qu25vgq8aj" data-mpi={i} />)}
    </>;
}
export function OctImage({
  revealed = [],
  halo = null,
  region = null,
  integrity = 'partial',
  hints = false,
  scanning = false,
  drift = false,
  zoom = false,
  className = ''
}: OctImageProps) {
  const has = (id: LayerId) => revealed.includes(id);
  const dim = (id: LayerId) => halo && halo !== id ? 0.35 : 1;
  return <div className={`relative overflow-hidden bg-[#0A0F14] ${className}`} data-id="element-101" data-mp="32m5mv2mky2nm">
      <motion.div className="relative" animate={zoom ? {
      scale: 1.9,
      x: '-8%',
      y: '-6%'
    } : drift ? {
      scale: [1, 1.05, 1]
    } : {
      scale: 1,
      x: 0,
      y: 0
    }} transition={drift && !zoom ? {
      duration: 16,
      repeat: Infinity,
      ease: 'linear'
    } : {
      duration: 0.3,
      ease: EASE
    }} data-id="element-102" data-mp="2xp6twnenk1r0">
        <img src={heroOct.src} alt={heroOct.alt} className="block w-full select-none" draggable={false} data-id="element-103" data-mp="3ghhqjzipr3go" />

        {region && <motion.span key={region} initial={{
        opacity: 0
      }} animate={{
        opacity: 0.28
      }} transition={{
        duration: 0.26,
        ease: EASE
      }} className="absolute rounded-xl" style={{
        left: `${REGIONS[region].left}%`,
        top: `${REGIONS[region].top}%`,
        width: `${REGIONS[region].width}%`,
        height: `${REGIONS[region].height}%`,
        backgroundColor: REGIONS[region].color
      }} aria-hidden="true" data-id="element-104" data-mp="251t3l85gm4fv" />}

        {has('srf') && <motion.span initial={{
        opacity: 0
      }} animate={{
        opacity: dim('srf')
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="absolute rounded-[40%] border-2 border-[#A99BFF]" style={{
        left: `${SRF.left}%`,
        top: `${SRF.top}%`,
        width: `${SRF.width}%`,
        height: `${SRF.height}%`,
        backgroundColor: halo === 'srf' ? 'rgba(169,155,255,0.22)' : 'transparent'
      }} aria-hidden="true" data-id="element-105" data-mp="bhr7siualr4h" />}

        {has('irf') && CYSTS.map((c, i) => <motion.span key={`${c.left}-${c.top}`} initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: dim('irf'),
        scale: 1
      }} transition={{
        duration: 0.24,
        delay: i * 0.04,
        ease: EASE
      }} className="absolute rounded-[50%] border-2 border-[#5CB2FF]" style={{
        left: `${c.left}%`,
        top: `${c.top}%`,
        width: `${c.width}%`,
        height: `${c.height}%`,
        backgroundColor: halo === 'irf' ? 'rgba(92,178,255,0.18)' : 'transparent'
      }} aria-hidden="true" data-id="element-106" data-mp="1984ff2lydv60" data-mpi={i} />)}

        {has('hrf') && FOCI.map((f, i) => <motion.span key={`${f.left}-${f.top}`} initial={{
        opacity: 0
      }} animate={{
        opacity: dim('hrf')
      }} transition={{
        duration: 0.18,
        delay: i * 0.04,
        ease: EASE
      }} className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
        left: `${f.left}%`,
        top: `${f.top}%`,
        backgroundColor: '#F5B968',
        boxShadow: halo === 'hrf' ? '0 0 0 4px rgba(245,185,104,0.28)' : 'none'
      }} aria-hidden="true" data-id="element-107" data-mp="6vgvg329q74n" data-mpi={i} />)}

        {has('elm') && <Band top={57} segments={BAND_SEGMENTS[integrity]} strong={halo === 'elm'} data-id="element-108" data-mp="1j0u7zxw87bwh" />}
        {has('ez') && <Band top={62} segments={BAND_SEGMENTS[integrity]} strong={halo === 'ez'} data-id="element-109" data-mp="1nzne3596o0mm" />}

        {hints && HINTS.map((h, i) => <motion.span key={`${h.left}-${h.top}`} initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.85, 0.85, 0]
      }} transition={{
        duration: 4.5,
        times: [0, 0.15, 0.7, 1],
        repeat: Infinity,
        delay: i * 0.8,
        ease: 'linear'
      }} className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-[#7FD4FF]" style={{
        left: `${h.left}%`,
        top: `${h.top}%`
      }} aria-hidden="true" data-id="element-110" data-mp="1t0b9020j91m4" data-mpi={i} />)}
      </motion.div>

      {scanning && <motion.span initial={{
      left: '-14%'
    }} animate={{
      left: '104%'
    }} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'linear'
    }} className="pointer-events-none absolute inset-y-0 w-[12%] bg-mint/30 blur-[6px]" aria-hidden="true" data-id="element-111" data-mp="izflgj4r00u3" />}
    </div>;
}